import React, { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

const DataMeshAnimation = () => {
  const controls = useAnimationControls();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    const runAnimation = async () => {
      try {
        await controls.start("reset");
        await controls.start("animate");
        await new Promise(resolve => setTimeout(resolve, 5000));
        if (isMounted) {
          runAnimation();
        }
      } catch (error) {
        console.error("Animation error:", error);
      }
    };
    
    runAnimation();
  }, [isMounted, controls]);

  return (
    <div className="relative w-full overflow-hidden">
      <motion.div 
        className="bg-[#EDE9FE] rounded-xl p-4 sm:p-6 relative h-[500px] sm:h-[400px] flex items-center justify-center transform-gpu"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Main Dashboard Container - Now full width */}
        <motion.div 
          className="bg-white rounded-lg shadow-lg w-full max-w-5xl overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={controls}
          variants={{
            reset: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0, transition: { delay: 0.5 } }
          }}
        >
          {/* Dashboard Header */}
          <div className="bg-purple-600 p-3 flex items-center">
            <div className="flex space-x-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <span className="text-white text-sm ml-3">Data Mesh Platform</span>
          </div>

          {/* Dashboard Content */}
          <div className="p-3 sm:p-4 space-y-3">
            {/* Enhanced Mesh Visualization - Now larger */}
            <motion.div
              className="bg-gray-50 rounded-lg p-3"
              initial={{ opacity: 0 }}
              animate={controls}
              variants={{
                reset: { opacity: 0 },
                animate: { opacity: 1, transition: { delay: 1 } }
              }}
            >
              <h3 className="text-sm font-medium text-gray-700 mb-2">Domain Data Products</h3>
              
              {/* Larger Mesh Visualization - Reduced height */}
              <motion.div 
                className="relative h-36 mb-2"
                initial={{ opacity: 0 }}
                animate={controls}
                variants={{
                  reset: { opacity: 0 },
                  animate: { opacity: 1, transition: { delay: 1.2 } }
                }}
              >
                {/* Connection Lines and Data Flow Animations */}
                <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
                  {(() => {
                    // Define node positions for reference
                    const nodePositions = [
                      { id: "sales", x: "10%", y: "20%" },
                      { id: "marketing", x: "45%", y: "10%" },
                      { id: "finance", x: "75%", y: "20%" },
                      { id: "product", x: "25%", y: "55%" },
                      { id: "customer", x: "60%", y: "60%" },
                      { id: "analytics", x: "10%", y: "75%" },
                      { id: "operations", x: "80%", y: "70%" }
                    ];
                    
                    // Create connections between nodes
                    const connections = [
                      { from: "sales", to: "marketing", delay: 1.8, color: "#6366F1", flowDelay: 3.0 },
                      { from: "marketing", to: "finance", delay: 1.9, color: "#10B981" },
                      { from: "sales", to: "product", delay: 2.0, color: "#6366F1" },
                      { from: "product", to: "customer", delay: 2.1, color: "#EF4444", flowDelay: 3.2 },
                      { from: "customer", to: "finance", delay: 2.2, color: "#3B82F6" },
                      { from: "marketing", to: "customer", delay: 2.3, color: "#10B981" },
                      { from: "analytics", to: "product", delay: 2.4, color: "#8B5CF6", flowDelay: 3.4 },
                      { from: "customer", to: "operations", delay: 2.5, color: "#3B82F6", flowDelay: 3.6 },
                      { from: "analytics", to: "sales", delay: 2.6, color: "#8B5CF6" },
                      { from: "operations", to: "finance", delay: 2.7, color: "#14B8A6" }
                    ];
                    
                    // Process connections to add coordinates
                    const processedConnections = connections.map(conn => {
                      const fromNode = nodePositions.find(n => n.id === conn.from);
                      const toNode = nodePositions.find(n => n.id === conn.to);
                      
                      // Calculate adjusted coordinates for center of nodes
                      const fromX = (parseInt(fromNode.x.replace('%', '')) + 5) + '%';
                      const fromY = (parseInt(fromNode.y.replace('%', '')) + 10) + '%';
                      const toX = (parseInt(toNode.x.replace('%', '')) + 5) + '%';
                      const toY = (parseInt(toNode.y.replace('%', '')) + 10) + '%';
                      
                      return {
                        ...conn,
                        fromX,
                        fromY,
                        toX,
                        toY
                      };
                    });
                    
                    // Render both connection lines and data flows
                    return (
                      <>
                        {/* Connection Lines */}
                        {processedConnections.map((line, i) => (
                          <motion.line
                            key={i}
                            x1={line.fromX}
                            y1={line.fromY}
                            x2={line.toX}
                            y2={line.toY}
                            stroke={line.color}
                            strokeWidth="1.5"
                            strokeDasharray="4 2"
                            initial={{ opacity: 0 }}
                            animate={controls}
                            variants={{
                              reset: { opacity: 0 },
                              animate: { 
                                opacity: 0.7,
                                transition: { delay: line.delay, duration: 0.3 } 
                              }
                            }}
                          />
                        ))}
                        
                        {/* Data Flow Animations */}
                        {processedConnections
                          .filter(conn => conn.flowDelay) // Only animate connections with flowDelay
                          .map((flow, i) => (
                            <motion.circle
                              key={`flow-${i}`}
                              r="3"
                              fill={flow.color}
                              initial={{ opacity: 0 }}
                              animate={controls}
                              variants={{
                                reset: { opacity: 0, cx: flow.fromX, cy: flow.fromY },
                                animate: { 
                                  opacity: [0, 1, 1, 0],
                                  cx: [flow.fromX, flow.fromX, flow.toX, flow.toX],
                                  cy: [flow.fromY, flow.fromY, flow.toY, flow.toY],
                                  transition: { 
                                    delay: flow.flowDelay, 
                                    duration: 1.2,
                                    times: [0, 0.1, 0.9, 1]
                                  } 
                                }
                              }}
                            />
                          ))
                        }
                      </>
                    );
                  })()}
                </svg>
                
                {/* Domain Nodes - More nodes and better positioned */}
                {[
                  { id: "sales", x: "10%", y: "20%", label: "Sales", color: "bg-indigo-500", size: "w-10 h-10" },
                  { id: "marketing", x: "45%", y: "10%", label: "Marketing", color: "bg-green-500", size: "w-10 h-10" },
                  { id: "finance", x: "75%", y: "20%", label: "Finance", color: "bg-yellow-500", size: "w-10 h-10" },
                  { id: "product", x: "25%", y: "55%", label: "Product", color: "bg-red-500", size: "w-10 h-10" },
                  { id: "customer", x: "60%", y: "60%", label: "Customer", color: "bg-blue-500", size: "w-10 h-10" },
                  { id: "analytics", x: "10%", y: "75%", label: "Analytics", color: "bg-purple-500", size: "w-9 h-9" },
                  { id: "operations", x: "80%", y: "70%", label: "Operations", color: "bg-teal-500", size: "w-9 h-9" }
                ].map((node, i) => (
                  <motion.div
                    key={i}
                    className="absolute flex flex-col items-center"
                    style={{ left: node.x, top: node.y, zIndex: 1, transform: "translate(-50%, -50%)" }}
                    initial={{ scale: 0 }}
                    animate={controls}
                    variants={{
                      reset: { scale: 0 },
                      animate: { scale: 1, transition: { delay: 1.3 + (i * 0.1) } }
                    }}
                  >
                    <div className={`${node.size} rounded-full ${node.color} flex items-center justify-center text-white text-sm font-bold shadow-md`}>
                      {node.label.charAt(0)}
                    </div>
                    <span className="text-xs mt-1 font-medium">{node.label}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Self-Serve Capabilities - More compact */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { 
                  title: 'Data Catalog', 
                  metric: '120 assets',
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                    </svg>
                  )
                },
                { 
                  title: 'Governance', 
                  metric: '7 domains',
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  )
                },
                { 
                  title: 'Data Quality', 
                  metric: '98% SLA',
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  )
                }
              ].map((capability, i) => (
                <motion.div
                  key={i}
                  className="bg-gray-50 rounded-lg p-1.5"
                  initial={{ opacity: 0, y: 10 }}
                  animate={controls}
                  variants={{
                    reset: { opacity: 0, y: 10 },
                    animate: { opacity: 1, y: 0, transition: { delay: 3.8 + (i * 0.2) } }
                  }}
                >
                  <div className="flex items-center mb-1">
                    <div className="mr-1">{capability.icon}</div>
                    <div className="text-xs font-medium text-gray-700">{capability.title}</div>
                  </div>
                  <div className="text-sm font-semibold text-indigo-600">{capability.metric}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Processing Indicator */}
        <motion.div
          className="absolute bottom-2 right-2 flex space-x-1"
          animate={{
            opacity: [0.3, 1, 0.3],
          }}
          transition={{
            repeat: Infinity,
            duration: 1.5,
            ease: "linear"
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1 h-1 bg-purple-500 rounded-full"
              style={{
                animationDelay: `${i * 0.2}s`
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DataMeshAnimation; 